import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Loader2,
  Search,
  Ship,
} from "lucide-react";

import { useUser } from "@/auth/UserContext";
import { cn } from "@/lib/utils";
import { buildBookingsFilterUrl } from "@/lib/bookingsFilterLink";
import { normalizeContractRef } from "@/lib/formatContract";
import {
  formatLinkedScopeNames,
  isFridayInTz,
} from "@/lib/myBookingsScope";
import { formatRouteLabel } from "@/lib/formatPort";
import { canExpandLinkedSearchScope } from "@/lib/viewerScopeFilters";
import { useGlobalSearchUI } from "@/context/GlobalSearchContext";
import { useUserPreferences } from "@/context/UserPreferencesContext";
import { BookingStateBadge } from "@/features/bookings/BookingStateBadge";
import { BookingReference } from "@/features/bookings/BookingReference";
import { CarrierLogo } from "@/components/common/CarrierLogo";
import { LinkedScopeSearchHint } from "@/components/common/LinkedScopeSearchHint";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import type { ApiBooking, RateCardSummary } from "@/types/api";
import type { BookingState } from "@/types/booking";

import { type SearchPage } from "./pageRegistry";
import { useGlobalSearch } from "./useGlobalSearch";

type SearchItem =
  | { kind: "page"; page: SearchPage }
  | { kind: "booking"; booking: ApiBooking }
  | { kind: "rateCard"; card: RateCardSummary }
  | { kind: "action"; action: "viewAllBookings" };

function SectionLabel({ children }: { children: string }) {
  return (
    <p className="px-3 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
      {children}
    </p>
  );
}

function ResultRow({
  active,
  onMouseEnter,
  onSelect,
  icon,
  title,
  subtitle,
  trailing,
}: {
  active: boolean;
  onMouseEnter: () => void;
  onSelect: () => void;
  icon: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  trailing?: ReactNode;
}) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={active}
      className={cn(
        "flex min-h-11 w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors",
        active ? "bg-accent/80 text-accent-foreground" : "hover:bg-accent/45",
      )}
      onMouseEnter={onMouseEnter}
      onClick={onSelect}
    >
      <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium">{title}</span>
        {subtitle ? (
          <span className="mt-0.5 block truncate text-xs text-muted-foreground">
            {subtitle}
          </span>
        ) : null}
      </span>
      {trailing ? <span className="shrink-0">{trailing}</span> : null}
    </button>
  );
}

function useFlatItems(
  results: ReturnType<typeof useGlobalSearch>["results"],
): SearchItem[] {
  return useMemo(() => {
    const items: SearchItem[] = [];
    for (const page of results.pages) items.push({ kind: "page", page });
    for (const card of results.rateCards) items.push({ kind: "rateCard", card });
    for (const booking of results.bookings) {
      items.push({ kind: "booking", booking });
    }
    if (results.totalBookings > results.bookings.length) {
      items.push({ kind: "action", action: "viewAllBookings" });
    }
    return items;
  }, [results]);
}

export function GlobalSearchDialog() {
  const navigate = useNavigate();
  const { open, setOpen, closeSearch } = useGlobalSearchUI();
  const { isSalesPerson, isClient, profile } = useUser();
  const { preferences, setScopeToMyBookings } = useUserPreferences();
  const showLocodes = preferences.showLocodes;
  const { query, setQuery, results, loading, reset } = useGlobalSearch(open);
  const canExpandScope = canExpandLinkedSearchScope({
    isSalesPerson,
    isClient,
    salesPersonLink: profile?.sales_person_link,
    openedByValues: profile?.opened_by_values,
    preferenceOn: preferences.scopeToMyBookings,
    fridayOverride: isFridayInTz(),
  });
  const scopeNames = formatLinkedScopeNames({
    salesPersonLink: profile?.sales_person_link,
    openedByValues: profile?.opened_by_values,
  });
  const expandSearchScope = useCallback(() => {
    setScopeToMyBookings(false);
  }, [setScopeToMyBookings]);
  const inputRef = useRef<HTMLInputElement>(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const flatItems = useFlatItems(results);

  const close = useCallback(() => {
    closeSearch();
    reset();
    setActiveIndex(-1);
  }, [closeSearch, reset]);

  const runItem = useCallback(
    (item: SearchItem) => {
      if (item.kind === "page") {
        navigate(item.page.href);
      } else if (item.kind === "booking") {
        navigate(`/bookings/${encodeURIComponent(item.booking.source_id)}`);
      } else if (item.kind === "rateCard") {
        navigate(`/rates/${encodeURIComponent(item.card.id)}`);
      } else if (item.kind === "action") {
        navigate(buildBookingsFilterUrl({ search: query.trim() }));
      }
      close();
    },
    [close, navigate, query],
  );

  useEffect(() => {
    if (!open) return;
    const frame = requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    });
    return () => cancelAnimationFrame(frame);
  }, [open]);

  useEffect(() => {
    setActiveIndex(flatItems.length > 0 ? 0 : -1);
  }, [flatItems]);

  const onInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (flatItems.length === 0) return;
      setActiveIndex((prev) => (prev + 1) % flatItems.length);
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      if (flatItems.length === 0) return;
      setActiveIndex((prev) =>
        prev <= 0 ? flatItems.length - 1 : prev - 1,
      );
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      const item = flatItems[activeIndex];
      if (item) runItem(item);
      else if (query.trim().length >= 2) {
        navigate(buildBookingsFilterUrl({ search: query.trim() }));
        close();
      }
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      close();
    }
  };

  const trimmed = query.trim();
  const isDefaultView = trimmed.length < 2;
  const hasResults = flatItems.length > 0;
  const showEmpty =
    !loading && !hasResults && !isDefaultView && trimmed.length >= 2;
  const showScopeHint = canExpandScope && showEmpty;
  const scopeHintVariant = "empty";

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
        else setOpen(true);
      }}
    >
      <DialogContent
        className="glass-overlay w-[min(36rem,calc(100vw-1.5rem))] overflow-hidden rounded-2xl border border-border/50 p-0 shadow-2xl"
        aria-describedby={undefined}
      >
        <DialogTitle className="sr-only">Global search</DialogTitle>
        <DialogDescription className="sr-only">
          Search pages, bookings, and rate cards
        </DialogDescription>

        <div className="flex items-center gap-3 border-b border-border/50 px-4 py-3">
          <Search className="size-4 shrink-0 text-muted-foreground" />
          <Input
            ref={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onInputKeyDown}
            placeholder="Search pages, bookings, rate cards…"
            className="h-11 border-0 bg-transparent px-0 shadow-none focus-visible:ring-0 md:h-9"
            autoComplete="off"
            spellCheck={false}
          />
          {loading ? (
            <Loader2 className="size-4 shrink-0 animate-spin text-muted-foreground" />
          ) : (
            <kbd className="hidden rounded-md border border-border/60 bg-muted/40 px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground sm:inline">
              esc
            </kbd>
          )}
        </div>

        {showScopeHint ? (
          <div className="border-b border-border/40 px-3 py-2">
            <LinkedScopeSearchHint
              compact
              variant={scopeHintVariant}
              scopeNames={scopeNames}
              onExpand={expandSearchScope}
            />
          </div>
        ) : null}

        <ScrollArea className="max-h-[min(24rem,calc(var(--app-vvh,var(--app-vh,100vh))-12rem))]">
          <div className="px-1.5 pb-2" role="listbox" aria-label="Search results">
            {showEmpty ? (
              <p className="px-3 py-8 text-center text-sm text-muted-foreground">
                No results for &ldquo;{trimmed}&rdquo;
              </p>
            ) : null}

            {results.pages.length > 0 ? (
              <div>
                <SectionLabel>
                  {isDefaultView ? "Go to" : "Pages"}
                </SectionLabel>
                {results.pages.map((page, index) => {
                  const flatIndex = index;
                  const Icon = page.icon;
                  return (
                    <ResultRow
                      key={page.id}
                      active={activeIndex === flatIndex}
                      onMouseEnter={() => setActiveIndex(flatIndex)}
                      onSelect={() => runItem({ kind: "page", page })}
                      icon={<Icon className="size-4" />}
                      title={page.title}
                      subtitle={`${page.description} · ${page.section}`}
                      trailing={<ArrowRight className="size-3.5 opacity-40" />}
                    />
                  );
                })}
              </div>
            ) : null}

            {results.rateCards.length > 0 ? (
              <div>
                <SectionLabel>Rate cards</SectionLabel>
                {results.rateCards.map((card, index) => {
                  const flatIndex = results.pages.length + index;
                  return (
                    <ResultRow
                      key={card.id}
                      active={activeIndex === flatIndex}
                      onMouseEnter={() => setActiveIndex(flatIndex)}
                      onSelect={() => runItem({ kind: "rateCard", card })}
                      icon={<CarrierLogo carrier={card.carrier} size={16} />}
                      title={
                        <span className="inline-flex items-center gap-2">
                          <CarrierLogo carrier={card.carrier} size={14} bold />
                          {card.contract_ref ? (
                            <span className="truncate">
                              {normalizeContractRef(
                                card.contract_ref,
                                card.carrier,
                              )}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">Rate card</span>
                          )}
                        </span>
                      }
                      subtitle={`${card.line_count} lines · ${card.source}`}
                      trailing={
                        <Badge variant="secondary" className="text-[10px] capitalize">
                          {card.status}
                        </Badge>
                      }
                    />
                  );
                })}
              </div>
            ) : null}

            {results.bookings.length > 0 ? (
              <div>
                <SectionLabel>Bookings</SectionLabel>
                {results.bookings.map((booking, index) => {
                  const flatIndex =
                    results.pages.length + results.rateCards.length + index;
                  return (
                    <ResultRow
                      key={booking.source_id}
                      active={activeIndex === flatIndex}
                      onMouseEnter={() => setActiveIndex(flatIndex)}
                      onSelect={() => runItem({ kind: "booking", booking })}
                      icon={<Ship className="size-4" />}
                      title={
                        <span className="inline-flex items-center gap-2">
                          <BookingReference
                            sourceId={booking.source_id}
                            fileId={booking.file_id}
                          />
                          {booking.carrier ? (
                            <CarrierLogo carrier={booking.carrier} size={14} />
                          ) : null}
                        </span>
                      }
                      subtitle={formatRouteLabel(
                        booking.origin_port,
                        booking.destination_port,
                        showLocodes,
                      )}
                      trailing={
                        <BookingStateBadge state={booking.state as BookingState} />
                      }
                    />
                  );
                })}
              </div>
            ) : null}

            {results.totalBookings > results.bookings.length ? (
              <div className="px-1.5 pt-1">
                <button
                  type="button"
                  role="option"
                  aria-selected={
                    activeIndex === flatItems.length - 1 &&
                    flatItems[flatItems.length - 1]?.kind === "action"
                  }
                  className={cn(
                    "flex w-full items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-sm text-primary transition-colors",
                    activeIndex === flatItems.length - 1
                      ? "bg-accent/80"
                      : "hover:bg-accent/45",
                  )}
                  onMouseEnter={() => setActiveIndex(flatItems.length - 1)}
                  onClick={() =>
                    runItem({ kind: "action", action: "viewAllBookings" })
                  }
                >
                  <Search className="size-3.5" />
                  View all {results.totalBookings} booking matches
                </button>
              </div>
            ) : null}

            {isDefaultView && hasResults ? (
              <p className="px-3 pb-3 pt-2 text-xs text-muted-foreground">
                Type at least 2 characters to search bookings and rate cards.
              </p>
            ) : null}
          </div>
        </ScrollArea>

        <div className="flex items-center justify-between border-t border-border/50 px-4 py-2 text-[11px] text-muted-foreground">
          <span className="hidden sm:inline">Navigate with ↑ ↓ · Select with ↵</span>
          <span className="sm:hidden">Tap a result to open</span>
          <kbd className="hidden rounded-md border border-border/60 bg-muted/40 px-1.5 py-0.5 font-medium sm:inline">
            ⌘K
          </kbd>
        </div>
      </DialogContent>
    </Dialog>
  );
}
